/**
 * 統一的錯誤處理工具
 * 負責處理和格式化各種錯誤，並記錄錯誤日誌
 */

import { Toast } from 'antd-mobile';

import { LogService } from './logService';

/**
 * API 錯誤響應接口
 */
export interface IApiError {
  status?: number;
  data?: {
    msg?: string;
    message?: string;
    code?: string;
    errors?: Record<string, string[]>;
  };
  message?: string;
}

/**
 * 錯誤處理類
 */
export class ErrorHandler {
  /**
   * 處理 API 錯誤
   * @param error 錯誤對象
   * @param defaultMessage 默認錯誤訊息
   * @param showToast 是否顯示 Toast 提示
   * @returns 錯誤訊息
   */
  static handleApiError(
    error: unknown,
    defaultMessage: string = '操作失敗，請重試',
    showToast: boolean = true
  ): string {
    const apiError = error as IApiError;
    let errorMessage = defaultMessage;

    // 提取錯誤訊息
    if (apiError?.data?.msg) {
      errorMessage = apiError.data.msg;
    } else if (apiError?.data?.message) {
      errorMessage = apiError.data.message;
    } else if (apiError?.message) {
      errorMessage = apiError.message;
    }

    // 記錄錯誤日誌
    LogService.logApiError('API Error', {
      status: apiError?.status,
      message: errorMessage,
      code: apiError?.data?.code,
      errors: apiError?.data?.errors,
      originalError: error,
    });

    // 顯示 Toast 提示
    if (showToast) {
      Toast.show({
        content: errorMessage,
        position: 'center',
      });
    }

    return errorMessage;
  }

  /**
   * 處理表單驗證錯誤
   * @param errors 驗證錯誤對象
   * @param showToast 是否顯示 Toast 提示
   * @returns 是否為空錯誤
   */
  static handleValidationErrors(
    errors: Record<string, string>,
    showToast: boolean = true
  ): boolean {
    if (Object.keys(errors).length === 0) {
      return true;
    }

    // 獲取第一個錯誤訊息
    const firstError = Object.values(errors)[0];

    // 記錄驗證錯誤日誌
    LogService.warn('Form Validation Failed', {
      errors,
      firstError,
    });

    // 顯示 Toast 提示
    if (showToast && firstError) {
      Toast.show({
        content: firstError,
        position: 'center',
      });
    }

    return false;
  }

  /**
   * 處理網絡錯誤
   * @param error 錯誤對象
   * @param showToast 是否顯示 Toast 提示
   */
  static handleNetworkError(
    error: unknown,
    showToast: boolean = true
  ): void {
    const errorMessage = '網絡連接失敗，請檢查網絡連接後重試';

    // 記錄網絡錯誤日誌
    LogService.error('Network Error', error);

    // 顯示 Toast 提示
    if (showToast) {
      Toast.show({
        content: errorMessage,
        position: 'center',
      });
    }
  }

  /**
   * 處理未知錯誤
   * @param error 錯誤對象
   * @param context 錯誤上下文
   * @param showToast 是否顯示 Toast 提示
   */
  static handleUnknownError(
    error: unknown,
    context: string = 'Unknown Error',
    showToast: boolean = true
  ): void {
    const errorMessage = '發生未知錯誤，請重試或聯繫技術支援';

    // 記錄未知錯誤日誌
    LogService.error(context, error);

    // 顯示 Toast 提示
    if (showToast) {
      Toast.show({
        content: errorMessage,
        position: 'center',
      });
    }
  }

  /**
   * 格式化驗證錯誤訊息
   * @param errors 驗證錯誤對象
   * @returns 格式化的錯誤訊息
   */
  static formatValidationErrors(errors: Record<string, string>): string {
    const errorMessages = Object.values(errors);
    if (errorMessages.length === 0) {
      return '';
    }
    if (errorMessages.length === 1) {
      return errorMessages[0] || '';
    }
    return `請修正以下錯誤：\n${errorMessages.map((msg, index) => `${index + 1}. ${msg}`).join('\n')}`;
  }

  /**
   * 處理取消核銷錯誤
   * @param error 錯誤對象
   * @param defaultMessage 默認錯誤訊息
   * @param showToast 是否顯示 Toast 提示
   * @returns 錯誤訊息
   */
  static handleCancelVerificationError(
    error: unknown,
    defaultMessage: string = '取消核銷失敗，請重試',
    showToast: boolean = true
  ): string {
    return ErrorHandler.handleApiError(error, defaultMessage, showToast);
  }

  /**
   * 處理退款錯誤
   * @param error 錯誤對象
   * @param defaultMessage 默認錯誤訊息
   * @param showToast 是否顯示 Toast 提示
   * @returns 錯誤訊息
   */
  static handleRefundError(
    error: unknown,
    defaultMessage: string = '退款操作失敗，請重試',
    showToast: boolean = true
  ): string {
    return ErrorHandler.handleApiError(error, defaultMessage, showToast);
  }

  /**
   * 處理補充核銷錯誤
   * @param error 錯誤對象
   * @param defaultMessage 默認錯誤訊息
   * @param showToast 是否顯示 Toast 提示
   * @returns 錯誤訊息
   */
  static handleSupplementVerificationError(
    error: unknown,
    defaultMessage: string = '補充核銷失敗，請重試',
    showToast: boolean = true
  ): string {
    return ErrorHandler.handleApiError(error, defaultMessage, showToast);
  }

  /**
   * 處理批量操作錯誤
   * @param error 錯誤對象
   * @param defaultMessage 默認錯誤訊息
   * @param showToast 是否顯示 Toast 提示
   * @returns 錯誤訊息
   */
  static handleBatchOperationError(
    error: unknown,
    defaultMessage: string = '批量操作失敗，請重試',
    showToast: boolean = true
  ): string {
    return ErrorHandler.handleApiError(error, defaultMessage, showToast);
  }
}

// 導出函數別名以保持向後兼容
export const handleApiError = ErrorHandler.handleApiError.bind(ErrorHandler);
export const handleCancelVerificationError = ErrorHandler.handleCancelVerificationError.bind(ErrorHandler);
export const handleRefundError = ErrorHandler.handleRefundError.bind(ErrorHandler);
export const handleSupplementVerificationError = ErrorHandler.handleSupplementVerificationError.bind(ErrorHandler);
export const handleBatchOperationError = ErrorHandler.handleBatchOperationError.bind(ErrorHandler);
